/**
 * The Team tab's member list (docs/plans/oncall-line-plan.md 2.3, 5, 6.1,
 * 7.7; WP-11): the people whose phones ring, in the order they ring.
 *
 * One row per live member (inactive ones stay listed, greyed, and are
 * skipped by the hunt): the place in the order, the name, the number (in
 * full: this list is for line admins, the only readers the server sends full
 * staff numbers to, plan 5; masked to the last four digits otherwise), the
 * linked SiteOps user, whether a PIN is set (or locked), the last test
 * result, and the actions: Send test call, Edit, Set PIN, Move up and Move
 * down, Remove, and the active switch.
 *
 * Reordering: drag a row by its handle (pointer, from sm: up) or use the
 * Move buttons (touch and keyboard). Either calls onMove(memberId, toIndex);
 * the tab sends the whole new order (PUT /members/order).
 */
import { useState } from 'react';
import type { DragEvent } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  KeyRound,
  Link2,
  Loader2,
  Pencil,
  PhoneOutgoing,
  Trash2,
} from 'lucide-react';
import { iconBtnCls, secondaryBtnCls } from '../../../ui/styles';
import type { OncallMember } from '../../api/oncallApi';
import { formatDateTime, formatPhone, maskPhone } from '../../lib/format';
import Toggle from '../settings/Toggle';

/** A member row as GET /members sends it (phone_last4 rides beside the full number, plan 16 WP-06). */
export type TeamMember = OncallMember & { phone_last4?: string | null };

const pillBase = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium';
const pill = {
  gray: `${pillBase} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`,
  green: `${pillBase} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`,
  red: `${pillBase} bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300`,
  amber: `${pillBase} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`,
  blue: `${pillBase} bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300`,
};

/** The number as this reader may see it: full when the server sent it, else the last four digits. */
export function memberNumber(member: TeamMember): string {
  if (member.phone_e164) return formatPhone(member.phone_e164);
  return maskPhone(member.phone_last4 ?? null);
}

export interface MemberListProps {
  members: TeamMember[];
  timeZone: string | null | undefined;
  /** Members with a test call out (waiting for the result). */
  testing: ReadonlySet<string>;
  /** Members whose test call is being placed (the POST in flight). */
  placing: ReadonlySet<string>;
  /** Why no test call can be sent now (the button is disabled and says so), or null. */
  testBlocked: string | null;
  /** A reorder or another write is in flight: the order controls wait. */
  reorderDisabled: boolean;
  /** The member whose active switch is being saved. */
  togglingId: string | null;
  onMove: (memberId: string, toIndex: number) => void;
  onToggleActive: (member: TeamMember, next: boolean) => void;
  onEdit: (member: TeamMember) => void;
  onPin: (member: TeamMember) => void;
  onTest: (member: TeamMember) => void;
  onRemove: (member: TeamMember) => void;
}

function PinStatus({ member, timeZone }: { member: TeamMember; timeZone: string | null | undefined }) {
  if (member.pin_locked_until) {
    return (
      <span className={pill.amber} data-testid={`oncall-member-pin-${member.id}`}>
        <KeyRound className="h-3 w-3" aria-hidden="true" />
        PIN locked until {formatDateTime(member.pin_locked_until, timeZone)}
      </span>
    );
  }
  return (
    <span className={member.pin_set ? pill.green : pill.gray} data-testid={`oncall-member-pin-${member.id}`}>
      <KeyRound className="h-3 w-3" aria-hidden="true" />
      {member.pin_set ? 'PIN set' : 'No PIN'}
    </span>
  );
}

function TestStatus({ member, timeZone, ringing }: { member: TeamMember; timeZone: string | null | undefined; ringing: boolean }) {
  const testId = `oncall-member-test-${member.id}`;
  if (ringing) {
    return (
      <span className={pill.blue} data-testid={testId} aria-live="polite">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        Test call ringing…
      </span>
    );
  }
  if (!member.last_test_at || member.last_test_ok === null || member.last_test_ok === undefined) {
    return (
      <span className={pill.amber} data-testid={testId}>
        Not tested
      </span>
    );
  }
  const when = formatDateTime(member.last_test_at, timeZone);
  return member.last_test_ok ? (
    <span className={pill.green} data-testid={testId}>
      Test passed {when}
    </span>
  ) : (
    <span className={pill.red} data-testid={testId}>
      Test failed {when}
    </span>
  );
}

export default function MemberList({
  members,
  timeZone,
  testing,
  placing,
  testBlocked,
  reorderDisabled,
  togglingId,
  onMove,
  onToggleActive,
  onEdit,
  onPin,
  onTest,
  onRemove,
}: MemberListProps) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const endDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  const dropOn = (targetId: string) => {
    const from = dragId;
    endDrag();
    if (!from || from === targetId || reorderDisabled) return;
    const toIndex = members.findIndex((m) => m.id === targetId);
    if (toIndex >= 0) onMove(from, toIndex);
  };

  let activePosition = 0;

  return (
    <ol className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800" data-testid="oncall-member-list">
      {members.map((member, index) => {
        const ringing = testing.has(member.id);
        const placingNow = placing.has(member.id);
        const inactive = !member.is_active;
        if (!inactive) activePosition += 1;
        const dragging = dragId === member.id;
        const dropTarget = overId === member.id && dragId !== null && dragId !== member.id;
        return (
          <li
            key={member.id}
            data-testid={`oncall-member-${member.id}`}
            draggable={!reorderDisabled}
            onDragStart={(e: DragEvent<HTMLLIElement>) => {
              if (reorderDisabled) return;
              setDragId(member.id);
              try {
                e.dataTransfer?.setData('text/plain', member.id);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
              } catch {
                // Some browsers refuse setData during some drags; the state above carries the id.
              }
            }}
            onDragOver={(e: DragEvent<HTMLLIElement>) => {
              if (!dragId) return;
              e.preventDefault();
              if (overId !== member.id) setOverId(member.id);
            }}
            onDragLeave={() => {
              if (overId === member.id) setOverId(null);
            }}
            onDrop={(e: DragEvent<HTMLLIElement>) => {
              e.preventDefault();
              dropOn(member.id);
            }}
            onDragEnd={endDrag}
            className={`flex flex-col gap-3 p-4 transition-colors duration-150 lg:flex-row lg:items-center ${
              dropTarget ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
            } ${dragging ? 'opacity-50' : ''}`}
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span
                aria-hidden="true"
                title="Drag to reorder"
                className={`mt-0.5 hidden text-gray-400 sm:inline-flex ${reorderDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-grab'}`}
              >
                <GripVertical className="h-4 w-4" />
              </span>
              <span
                className={`mt-0.5 inline-flex h-6 min-w-[1.5rem] flex-shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold tabular-nums ${
                  inactive
                    ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                }`}
                title={inactive ? 'Inactive: skipped by the hunt' : `Rings in position ${activePosition}`}
              >
                {inactive ? '–' : activePosition}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className={`text-sm font-medium ${inactive ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                    {member.display_name}
                  </span>
                  {inactive && <span className={pill.gray}>Inactive</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                  <span className="tabular-nums" data-testid={`oncall-member-number-${member.id}`}>
                    {memberNumber(member)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Link2 className="h-3 w-3" aria-hidden="true" />
                    {member.user_id ? member.user_name?.trim() || 'Linked user' : 'Not linked'}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <PinStatus member={member} timeZone={timeZone} />
                  <TestStatus member={member} timeZone={timeZone} ringing={ringing} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:justify-end">
              <Toggle
                checked={member.is_active}
                onChange={(next) => onToggleActive(member, next)}
                label={`Active: ${member.display_name}`}
                disabled={togglingId === member.id}
                showState
                onText="Active"
                offText="Inactive"
                testId={`oncall-member-active-${member.id}`}
              />
              <button
                type="button"
                onClick={() => onTest(member)}
                disabled={Boolean(testBlocked) || ringing || placingNow}
                title={testBlocked ?? undefined}
                data-testid={`oncall-member-testcall-${member.id}`}
                className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {placingNow || ringing ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <PhoneOutgoing className="h-4 w-4" aria-hidden="true" />
                )}
                Send test call
              </button>
              <button
                type="button"
                onClick={() => onEdit(member)}
                aria-label={`Edit ${member.display_name}`}
                title="Edit"
                data-testid={`oncall-member-edit-${member.id}`}
                className={iconBtnCls}
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onPin(member)}
                aria-label={`Set PIN for ${member.display_name}`}
                title="Set PIN"
                data-testid={`oncall-member-pin-button-${member.id}`}
                className={iconBtnCls}
              >
                <KeyRound className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onMove(member.id, index - 1)}
                disabled={reorderDisabled || index === 0}
                aria-label={`Move ${member.display_name} up`}
                title="Move up"
                data-testid={`oncall-member-up-${member.id}`}
                className={iconBtnCls}
              >
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onMove(member.id, index + 1)}
                disabled={reorderDisabled || index === members.length - 1}
                aria-label={`Move ${member.display_name} down`}
                title="Move down"
                data-testid={`oncall-member-down-${member.id}`}
                className={iconBtnCls}
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(member)}
                aria-label={`Remove ${member.display_name}`}
                title="Remove from the list"
                data-testid={`oncall-member-remove-${member.id}`}
                className={`${iconBtnCls} hover:border-red-300 hover:text-red-600 dark:hover:text-red-400`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
